import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import RAPDReport from "../RAPDReport";
import VisualFieldReport from "../VisualFieldReport";
import EstermanReport from "../EstermanReport";
import ColorVisionReport from "../ColorVisionReport";
import ContrastSensitivityReport from "../ContrastSensitivityReport";
import SingleThresholdReport from "../SingleThresholdReport";
import VisualAcuityReport from "../VisualAcuityReport";
import SynoptophoreReport from "../SynoptophoreReport";
import Breadcrumb from "../../../components/UI/ReportBreadcrumb";
import Loader from "../../../components/UI/Loader";
import DownloadButtons from "../../../components/UI/DownloadButtons";
import MultiExamReportPanel from "./MultiExamReportPanel";
import { TransferIcon } from "../../../assets/icons";

// MUI Components
import {
  Button,
  Box,
  ThemeProvider,
  createTheme,
  Typography,
} from "@mui/material";

// Utilities
import { ValidatePDFContext } from "../../../contexts/ValidatePDFContext";
import { ErrorMessageComponent } from "../../../components/UI/ErrorMessage";
import { ReportDataContext } from "../../../contexts/ReportDataContext";
import { ExamTransferContext } from "../../../contexts/ExamTransferContext";
import { examCardEnums } from "../../../enums";

// Styles
import { ModalCustomDark } from "../../../styles/muiStylesHelper";

const ReportPanel = ({
  downloadPdf,
  shouldDisplayReport,
  fileInfo,
  reportInfoError,
  shouldDisplayDicomButton,
  shouldDisplayPngButton,
}) => {
  const bpTheme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1300,
        xl: 1700,
      },
    },
  });
  const { t } = useTranslation();
  const {
    isLoading,
    patient,
    patientName,
    exam,
    reportData,
    fetchInitialReport,
  } = useContext(ReportDataContext);
  const { handleExamTransferClick } = useContext(ExamTransferContext);

  const [initialReportData, setInitialReportData] = useState([]);

  const currentExamSections = useMemo(() => {
    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );

    return examSectionsKeys.filter(
      (key) =>
        exam[key]?.length > 0 &&
        exam[key]?.some((examSection) => Boolean(examSection.completionDate))
    );
  }, [exam]);

  const isMultiExamPackage = useMemo(() => {
    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );

    return examSectionsKeys.filter((key) => exam[key]?.length > 0)?.length > 1;
  }, [exam]);

  const ReportComponent = useMemo(() => {
    // add section classNames to multi-exam packages only
    const sectionClassName = isMultiExamPackage
      ? "retinalogik-package"
      : undefined;

    return (
      <>
        {currentExamSections.map((sectionName) => {
          if (!reportData?.[sectionName]?.display) {
            return null;
          }

          const key =
            reportData?.[sectionName]?.sectionId ??
            reportData?.[sectionName]?.sectionId2;

          switch (sectionName) {
            case examCardEnums.COLOR_VISION.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <ColorVisionReport />
                </div>
              );
            case examCardEnums.CONTRAST_SENSITIVITY.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <ContrastSensitivityReport />
                </div>
              );
            case examCardEnums.VISUAL_ACUITY.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <VisualAcuityReport />
                </div>
              );
            case examCardEnums.SYNOPTOPHORE.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <SynoptophoreReport />
                </div>
              );
            case examCardEnums.RAPD.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <RAPDReport />
                </div>
              );
            case examCardEnums.VISUAL_FIELD_ESTERMAN.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <EstermanReport />
                </div>
              );
            case examCardEnums.VISUAL_FIELD_FULL_120.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <SingleThresholdReport />
                </div>
              );
            case examCardEnums.VISUAL_FIELD_SCREENING.sectionName:
            case examCardEnums.VISUAL_FIELD_THRESHOLDING.sectionName:
              return (
                <div key={key} className={sectionClassName}>
                  <VisualFieldReport sectionName={sectionName} />
                </div>
              );
            default:
              return null;
          }
        })}
      </>
    );
  }, [isMultiExamPackage, currentExamSections, reportData]);

  // check if the report exists
  useEffect(() => {
    const controller = new AbortController();
    fetchInitialReport?.(controller.signal).then((data) => {
      setInitialReportData(data);
    });
  }, [fetchInitialReport]);

  // if the report doesn't exist, don't show the download buttons
  const shouldDisplayDownloadButtons = useMemo(
    () => Boolean(initialReportData?.length > 0 && shouldDisplayReport),
    [initialReportData, shouldDisplayReport]
  );

  return (
    <>
      <ValidatePDFContext.Provider value={{ forBackendPdf: false }}>
        {isLoading ? (
          <Loader />
        ) : (
          <ThemeProvider theme={bpTheme}>
            <Breadcrumb
              isLoading={isLoading}
              patientId={patient?.id}
              patientName={patientName}
            />
            <Typography fontWeight="bold" my="16px">
              {t("word_report")}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap="16px" width="100%">
              <Button
                startIcon={<TransferIcon />}
                sx={{
                  ...ModalCustomDark(),
                  width: { xs: "100%", sm: "fit-content" },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleExamTransferClick(exam, patient?.id);
                }}
              >
                {t("word_transfer_exam")}
              </Button>
              {shouldDisplayDownloadButtons && (
                <DownloadButtons
                  isLoading={isLoading}
                  fileInfo={fileInfo}
                  reportInfoError={reportInfoError}
                  downloadPdf={downloadPdf}
                  shouldDisplayDicomButton={shouldDisplayDicomButton}
                  shouldDisplayPngButton={shouldDisplayPngButton}
                />
              )}
            </Box>
            {!shouldDisplayReport ? (
              <ErrorMessageComponent />
            ) : (
              <>
                {isMultiExamPackage && <MultiExamReportPanel exam={exam} />}
                {ReportComponent}
              </>
            )}
          </ThemeProvider>
        )}
      </ValidatePDFContext.Provider>
    </>
  );
};

export default ReportPanel;
